import { gsap, CSSPlugin } from 'gsap';
import { ScrollToPlugin } from 'gsap/all';

gsap.registerPlugin(CSSPlugin);
gsap.registerPlugin(ScrollToPlugin);

/**
 * Opens an element
 *
 * @example
 * import {open} from '../helpers';
 *
 * open({ element: this.el.querySelector('.element)});
 *
 * @param {Object} element - HTML element you want to target for animation
 * @param {Number} duration - Speed of the animation
 * @param {String} ease - Ease of the animation
 * @param {Function} onComplete - On complete function
 */

type openProps = {
  element: HTMLElement;
  duration?: number;
  ease?: string;
  onComplete?: () => void;
  height?: string;
};

export const open = ({
  element,
  duration = 0.4,
  ease = 'easeOut',
  onComplete,
  height = 'auto'
}: openProps) => {
  gsap.set(element, { height });
  gsap.from(element, {
    height: 0,
    ease: ease,
    duration,
    onComplete: () => {
      onComplete && onComplete();
    }
  });
};

/**
 * Closes an element
 *
 * @example
 * import {close} from '../helpers';
 *
 * close({ element: this.el.querySelector('.element)});
 *
 * @param {Object} element - HTML element you want to target for animation
 * @param {Number} duration - Speed of the animation
 * @param {String} ease - Ease of the animation
 * @param {Function} onComplete - On complete function
 */

type closeProps = {
  element: HTMLElement;
  duration?: number;
  ease?: string;
  onComplete?: () => void;
};

export const close = ({ element, duration = 0.4, ease = 'easeOut', onComplete }: closeProps) => {
  gsap.set(element, { height: element.offsetHeight });
  gsap.to(element, {
    height: 0,
    ease: ease,
    duration,
    onComplete: () => {
      onComplete && onComplete();
    }
  });
};

// /**
// * Checks browser is running on a native mobile device
// */

type ExtendedWindowType = typeof window & {
  opera: string;
  MSStream: boolean;
};
export const isDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as ExtendedWindowType).opera;

  if (
    /windows phone/i.test(userAgent) ||
    /android/i.test(userAgent) ||
    (/iPad|iPhone|iPod/.test(userAgent) && !(window as ExtendedWindowType).MSStream)
  ) {
    return true;
  }

  return false;
};

// eslint-disable-next-line
export const setTrackDataLayer: (data: { eventName: string, custom: any }) => void = ({ eventName, custom = {} }) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...custom
  });
};

export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  } else {
    return false;
  }
};


export const recommendArticlePost = (endpoint: string, data: string) => {
  fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
    },
    body: data
  }).then(response => response.json())
    .then(data => {
      console.log(data);
    }).catch(err => {
      console.error(err);
    });
}


export const addInnerContainer = () => {
  const innerContainer = document.querySelector('.main-content:has(.sidebar)');


  if (innerContainer && !(innerContainer as HTMLElement).dataset.disableInnerContainer) {
    innerContainer.classList.add('inner_container');
  }
};

export const addVideoModal = () => {
  const modalLinks = document.querySelectorAll('a.launch-media_modal');

  if (!modalLinks.length) {
    return;
  }

  modalLinks.forEach(link => {
    link.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a.launch-media_modal') as HTMLAnchorElement;
      e.preventDefault();

      const url = new URL(link.href);
      url.searchParams.append('type', 'modal');

      fetch(url.toString(), {
        headers: {
          'Accept': 'application/json'
        }
      })
        .then(response => response.json())
        .then(data => {
          const modalContent = data.modal;

          // Create new modal
          const modal = document.createElement('dialog');
          modal.className = 'modal modal--media';
          modal.setAttribute('data-module', 'modal');
          modal.setAttribute('data-modal-id', `modal-${modalContent.id}`);
          
          // Create modal template
          const modalTemplate = `
            <div class="modal__container">
              <div class="modal__header">
                <button class="modal__close" type="button" aria-label="Close modal">
                  <svg class="modal__close-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>
              <div class="modal__content rtf">
                ${modalContent.title ? `<h6>${modalContent.title}</h6>` : ''}
                ${modalContent.headline ? `<h2>${modalContent.headline}</h2>` : ''}
                
                ${modalContent.video_url ? `
                  <div class="media_container video">
                    <iframe src="${modalContent.video_url}"scrolling="no" allowfullscreen="" webkitallowfullscreen="" mozallowfullscreen=""></iframe>
                  </div>
                ` : modalContent.video_id ? `
                  <div class="media_container video">
                    <video 
                      data-video-id="${modalContent.video_id}"
                      data-account="${modalContent.video_account}"
                      data-player="default"
                      data-embed="default"
                      class="video-js"
                      controls>
                    </video>
                    <script src="//players.brightcove.net/1496514756001/default_default/index.min.js"></script>
                  </div>
                ` : modalContent.image_url ? `
                  <div class="media_container">
                    <img src="${modalContent.image_url}" alt="">
                  </div>
                ` : ''}

                ${modalContent.content || modalContent.file_url ? `
                  <div class="col2">
                    ${modalContent.content || ''}
                    ${modalContent.file_url ? `
                      <div style="margin-top: 30px;">
                        <a class="btn" href="${modalContent.file_url}" target="_blank">
                          ${modalContent.cta_label}
                        </a>
                      </div>
                    ` : ''}
                  </div>
                ` : ''}

                ${modalContent.additional_content ? `
                  <div class="additional_content">
                    ${modalContent.additional_content}
                  </div>
                ` : ''}
              </div>
            </div>
          `;

          // Add modal to DOM
          modal.innerHTML = modalTemplate;
          document.body.appendChild(modal);

          // Add click handlers for closing
          const closeModal = () => {
            modal.close();
            modal.remove(); // Remove from DOM when closed
          };

          modal.addEventListener('click', (event) => {
            const rect = modal.getBoundingClientRect();
            const isInDialog = (
              rect.top <= event.clientY &&
              event.clientY <= rect.top + rect.height &&
              rect.left <= event.clientX &&
              event.clientX <= rect.left + rect.width
            );
            if (!isInDialog) {
              closeModal();
            }
          });

          const closeButton = modal.querySelector('.modal__close');
          if (closeButton) {
            closeButton.addEventListener('click', closeModal);
          }

          modal.showModal();
        })
        .catch(error => {
          console.error('Error loading modal content:', error);
        });
    });
  });
};